/**
 * useBlockchainData Hook
 *
 * Smart React hook for accessing blockchain data with three-tier caching system.
 * Provides clean, optimized access to offers, game instances, and user-specific data
 * while minimizing RPC calls and maintaining real-time synchronization.
 *
 * Features:
 * - Automatic data fetching based on filters
 * - Real-time updates via blockchain events
 * - User-specific data optimization
 * - Loading and error state management
 * - Cache invalidation and refresh capabilities
 */

import { useState, useEffect, useCallback, useRef } from 'react';
import blockchainDataManager from '../services/BlockchainDataManager.js';

/**
 * Primary hook for accessing blockchain data
 *
 * @param {string} dataType - Type of data: 'offers', 'gameInstances', 'userOffers', 'userGameInstances'
 * @param {object} filters - Filtering options
 * @param {boolean} filters.activeOnly - Show only active items (default: true)
 * @param {string} filters.gameFilter - Filter by specific game name (default: '')
 * @param {string} filters.userAddress - For user-specific queries (default: null)
 * @param {boolean} filters.includeInactive - Include inactive items (triggers Tier 2 cache)
 * @param {boolean} autoFetch - Automatically fetch data when filters change (default: true)
 * @returns {object} { data, loading, error, refresh, stats }
 */
export const useBlockchainData = (dataType, filters = {}, autoFetch = true) => {
  const [data, setData] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [initialized, setInitialized] = useState(false);

  // Refs to prevent unnecessary re-renders
  const filtersRef = useRef(filters);
  const subscriptionsRef = useRef([]);
  const dataTypeRef = useRef(dataType);

  // Update refs when dependencies change
  useEffect(() => {
    filtersRef.current = filters;
  }, [filters]);

  useEffect(() => {
    dataTypeRef.current = dataType;
  }, [dataType]);

  /**
   * Fetch data from BlockchainDataManager
   */
  const fetchData = useCallback(async (overrideFilters = null) => {
    const currentFilters = overrideFilters || filtersRef.current;
    const currentDataType = dataTypeRef.current;

    try {
      setLoading(true);
      setError(null);

      console.log(`🔄 useBlockchainData: Fetching ${currentDataType} with filters:`, currentFilters);

      // Determine the base data type for the manager
      const baseDataType = currentDataType.startsWith('user')
        ? currentDataType.replace('user', '').replace(/^./, str => str.toLowerCase()) // Only lowercase first letter
        : currentDataType;

      // Create manager filters
      const managerFilters = {
        gameFilter: currentFilters.gameFilter || '',
        activeOnly: currentFilters.activeOnly !== false, // Default to true
        userAddress: currentFilters.userAddress || null,
        includeInactive: currentFilters.includeInactive || false,
        forceRefresh: currentFilters.forceRefresh || false
      };

      // For user-specific data types, set userAddress
      if (currentDataType.startsWith('user') && currentFilters.userAddress) {
        managerFilters.userAddress = currentFilters.userAddress;
      }

      const result = await blockchainDataManager.getData(baseDataType, managerFilters);
      setData(result || []);

      console.log(`✅ useBlockchainData: Fetched ${result?.length || 0} ${currentDataType} items`);

    } catch (err) {
      console.error(`❌ useBlockchainData: Error fetching ${currentDataType}:`, err);
      setError(err.message || 'Failed to fetch data');
      setData([]);
    } finally {
      setLoading(false);
    }
  }, []); // Remove dataType dependency

  /**
   * Refresh data (force refresh from blockchain)
   */
  const refresh = useCallback(() => {
    fetchData({ ...filtersRef.current, forceRefresh: true });
  }, [fetchData]);

  /**
   * Initialize hook and fetch initial data
   */
  useEffect(() => {
    // Setup subscriptions for real-time updates only once
    const currentDataType = dataTypeRef.current;
    const eventType = currentDataType.includes('offer') ? 'offersUpdated' : 'gameInstancesUpdated';

    const unsubscribe = blockchainDataManager.subscribe(eventType, (eventData) => {
      console.log(`📡 useBlockchainData: Received ${eventType} event, refreshing data`);
      fetchData(); // Refresh data when relevant events occur
    });

    subscriptionsRef.current.push(unsubscribe);

    // Subscribe to initialization events
    const initUnsubscribe = blockchainDataManager.subscribe('initialized', () => {
      console.log('📡 useBlockchainData: BlockchainDataManager initialized, fetching initial data');
      setInitialized(true);
      fetchData();
    });

    subscriptionsRef.current.push(initUnsubscribe);

    // Fetch initial data if manager is already initialized
    const managerStats = blockchainDataManager.getStats();
    if (managerStats.runtime > 0) { // Manager has been running
      setInitialized(true);
      if (autoFetch) {
        fetchData();
      }
    }

    // Cleanup subscriptions on unmount
    return () => {
      subscriptionsRef.current.forEach(unsubscribe => unsubscribe());
      subscriptionsRef.current = [];
    };
  }, []); // Remove all dependencies - this should only run once on mount

  /**
   * Re-fetch data when filters change (use JSON stringify to properly detect changes)
   */
  const filtersStringRef = useRef(JSON.stringify(filters));
  useEffect(() => {
    const currentFiltersString = JSON.stringify(filters);
    const filtersChanged = currentFiltersString !== filtersStringRef.current;

    if (filtersChanged && initialized && autoFetch) {
      filtersStringRef.current = currentFiltersString;
      fetchData();
    } else if (!filtersChanged && !filtersStringRef.current && initialized && autoFetch) {
      // Initial fetch for empty filters
      filtersStringRef.current = currentFiltersString;
      fetchData();
    }
  }, [filters, initialized, autoFetch]);

  return {
    data,
    loading,
    error,
    refresh,
    stats: blockchainDataManager.getStats(),
    initialized
  };
};

/**
 * Specialized hook for offers data
 */
export const useOffers = (filters = {}, autoFetch = true) => {
  return useBlockchainData('offers', filters, autoFetch);
};

/**
 * Specialized hook for game instances data
 */
export const useGameInstances = (filters = {}, autoFetch = true) => {
  return useBlockchainData('gameInstances', filters, autoFetch);
};

/**
 * Specialized hook for user-specific offers
 */
export const useUserOffers = (userAddress, additionalFilters = {}, autoFetch = true) => {
  const filters = {
    userAddress,
    activeOnly: false, // User wants to see their own inactive offers
    ...additionalFilters
  };

  return useBlockchainData('userOffers', filters, autoFetch && !!userAddress);
};

/**
 * Specialized hook for user-specific game instances
 */
export const useUserGameInstances = (userAddress, additionalFilters = {}, autoFetch = true) => {
  const filters = {
    userAddress,
    activeOnly: false, // User wants to see their own inactive games
    ...additionalFilters
  };

  return useBlockchainData('userGameInstances', filters, autoFetch && !!userAddress);
};

/**
 * Hook for PersonalizedSection - optimized for user-centric view
 */
export const usePersonalizedData = (userAddress) => {
  const {
    data: userOffers,
    loading: userOffersLoading,
    error: userOffersError,
    refresh: refreshUserOffers
  } = useUserOffers(userAddress);

  const {
    data: userGameInstances,
    loading: userGameInstancesLoading,
    error: userGameInstancesError,
    refresh: refreshUserGameInstances
  } = useUserGameInstances(userAddress);

  const {
    data: activeOffers,
    loading: activeOffersLoading,
    error: activeOffersError,
    refresh: refreshActiveOffers
  } = useOffers({ activeOnly: true });

  const {
    data: activeGameInstances,
    loading: activeGameInstancesLoading,
    error: activeGameInstancesError,
    refresh: refreshActiveGameInstances
  } = useGameInstances({ activeOnly: true });

  const loading = userOffersLoading || userGameInstancesLoading ||
                 activeOffersLoading || activeGameInstancesLoading;

  const error = userOffersError || userGameInstancesError ||
               activeOffersError || activeGameInstancesError;

  const refresh = useCallback(() => {
    refreshUserOffers();
    refreshUserGameInstances();
    refreshActiveOffers();
    refreshActiveGameInstances();
  }, [refreshUserOffers, refreshUserGameInstances, refreshActiveOffers, refreshActiveGameInstances]);

  return {
    // User-specific data (includes inactive)
    userOffers,
    userGameInstances,

    // Global active data (for finding targeted offers, etc.)
    activeOffers,
    activeGameInstances,

    // Combined state
    loading,
    error,
    refresh,

    // Individual refresh functions
    refreshUserOffers,
    refreshUserGameInstances,
    refreshActiveOffers,
    refreshActiveGameInstances
  };
};

/**
 * Hook for GeneralSection - optimized for filtered views
 */
export const useGeneralData = (filters = {}) => {
  const {
    data: offers,
    loading: offersLoading,
    error: offersError,
    refresh: refreshOffers
  } = useOffers(filters);

  const {
    data: gameInstances,
    loading: gameInstancesLoading,
    error: gameInstancesError,
    refresh: refreshGameInstances
  } = useGameInstances(filters);

  const loading = offersLoading || gameInstancesLoading;
  const error = offersError || gameInstancesError;

  const refresh = useCallback(() => {
    refreshOffers();
    refreshGameInstances();
  }, [refreshOffers, refreshGameInstances]);

  return {
    offers,
    gameInstances,
    loading,
    error,
    refresh
  };
};

/**
 * Hook for blockchain manager statistics and control
 */
export const useBlockchainStats = () => {
  const [stats, setStats] = useState(blockchainDataManager.getStats());

  useEffect(() => {
    const interval = setInterval(() => {
      setStats(blockchainDataManager.getStats());
    }, 5000); // Update stats every 5 seconds

    return () => clearInterval(interval);
  }, []);

  const clearCache = useCallback((tier = 'all') => {
    if (tier === 'all' || tier === 'tier1') {
      blockchainDataManager.tier1Cache.clear();
    }
    if (tier === 'all' || tier === 'tier2') {
      blockchainDataManager.tier2Cache.clear();
    }
    console.log(`🧹 Cleared ${tier} cache`);
  }, []);

  return {
    stats,
    clearCache
  };
};

export default useBlockchainData;